import { useState, type FormEvent, type ReactNode } from 'react';
import {
  can,
  emailSchema,
  MEMBER_ROLE_LABELS,
  type IssuedInvitationView,
} from '@cercle/shared';
import { useApi, useApp } from '@/shared/app-context';
import { useAsync } from '@/shared/hooks/useAsync';
import { errorMessage } from '@/shared/http/http-client';
import {
  Avatar,
  Button,
  Card,
  CopyField,
  EmptyState,
  ErrorText,
  Input,
  Kicker,
  Muted,
  Skeleton,
  Status,
} from '@/shared/ui';
import styles from './admin.module.scss';

/** Écran 08 — régie des membres (US-01, US-04), visible du seul fondateur. */
export function AdminMembersScreen(): ReactNode {
  const api = useApi();
  const { member: me } = useApp();
  const members = useAsync(() => api.admin.members(), []);
  const invitations = useAsync(() => api.admin.invitations(), []);

  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // La dernière invitation émise, le temps de copier son lien. Elle ne survit pas au
  // rechargement de la page : le jeton n'existe qu'ici, et nulle part ailleurs.
  const [issued, setIssued] = useState<IssuedInvitationView | null>(null);

  async function invite(event: FormEvent): Promise<void> {
    event.preventDefault();
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setError('Adresse email invalide.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      setIssued(await api.admin.invite(parsed.data));
      setEmail('');
      invitations.reload();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function resend(invitationId: string): Promise<void> {
    setError(null);
    try {
      // Renvoyer révoque l'ancien lien et en fabrique un neuf : c'est aussi la seule façon
      // de récupérer un lien qu'on n'a pas copié à temps.
      setIssued(await api.admin.resendInvitation(invitationId));
      invitations.reload();
    } catch (cause) {
      setError(errorMessage(cause));
    }
  }

  async function act(action: () => Promise<unknown>): Promise<void> {
    try {
      await action();
      members.reload();
    } catch (cause) {
      setError(errorMessage(cause));
    }
  }

  const active = members.data?.filter((m) => m.status === 'ACTIVE').length ?? 0;
  const disabled = members.data?.filter((m) => m.status === 'DISABLED').length ?? 0;

  return (
    <div className={styles.page}>
      <header>
        <Kicker>Régie · fondateur</Kicker>
        <h1>Les membres</h1>
        <Muted>
          {active} actif{active > 1 ? 's' : ''} · {disabled} désactivé{disabled > 1 ? 's' : ''} ·{' '}
          {invitations.data?.length ?? 0} invitation(s) en cours
        </Muted>
      </header>

      <Card>
        <form className={styles.inviteForm} onSubmit={(event) => void invite(event)} noValidate>
          <Input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="email@invitation.fr"
            aria-label="Email à inviter"
            required
          />
          <Button type="submit" variant="dashed" disabled={busy}>
            {busy ? 'Envoi…' : '+ Inviter par email'}
          </Button>
        </form>
        <Muted>Lien à usage unique, expiration 7 jours. Aucune inscription publique.</Muted>
      </Card>

      {issued ? <IssuedInvitation invitation={issued} onDismiss={() => setIssued(null)} /> : null}

      <ErrorText>{error}</ErrorText>

      <section>
        <h2 className={styles.sectionTitle}>Invitations en attente</h2>
        {invitations.loading ? <Skeleton /> : null}
        {invitations.data?.length === 0 ? <EmptyState>Aucune invitation en cours.</EmptyState> : null}
        <ul className={styles.rows}>
          {invitations.data?.map((invitation) => (
            <li key={invitation.id} className={styles.row}>
              <span className={styles.rowInfo}>
                {/* Une adresse est une donnée, pas un titre : police courante, et
                    coupable n'importe où — sans quoi elle recouvrait la pastille d'état. */}
                <strong className={styles.rowEmail}>{invitation.email}</strong>
                <Muted>
                  {invitation.daysLeft} jour{invitation.daysLeft > 1 ? 's' : ''} restant
                  {invitation.daysLeft > 1 ? 's' : ''}
                </Muted>
              </span>
              <Status kind="ephemeral">Éphémère</Status>
              <Button variant="ghost" onClick={() => void resend(invitation.id)}>
                Renvoyer
              </Button>
              <Button
                variant="ghost"
                onClick={() =>
                  void api.admin.revokeInvitation(invitation.id).then(invitations.reload)
                }
              >
                Révoquer
              </Button>
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Le cercle</h2>
        {members.loading ? <Skeleton /> : null}
        <ul className={styles.rows}>
          {members.data?.map((member) => (
            <li key={member.id} className={styles.row}>
              <Avatar pseudo={member.pseudo} url={member.avatarUrl} />
              <span className={styles.rowInfo}>
                <strong>{member.pseudo}</strong>
                <Muted>
                  {MEMBER_ROLE_LABELS[member.role]} · {member.tastingCount} note
                  {member.tastingCount > 1 ? 's' : ''}
                </Muted>
              </span>
              <Status kind={member.status === 'ACTIVE' ? 'active' : 'disabled'}>
                {member.status === 'ACTIVE' ? 'Actif' : 'Désactivé'}
              </Status>
              {member.role !== 'ADMIN' ? (
                <>
                  {/* La nomination n'appartient qu'au fondateur : le bouton disparaît
                      pour un maître de chai, qui ne peut pas se choisir des pairs. */}
                  {me && can(me.role, 'member:promote') ? (
                    <Button
                      variant="ghost"
                      onClick={() =>
                        void act(() =>
                          api.admin.setMemberRole(
                            member.id,
                            member.role === 'MAITRE_DE_CHAI' ? 'BOUILLEUR' : 'MAITRE_DE_CHAI',
                          ),
                        )
                      }
                    >
                      {member.role === 'MAITRE_DE_CHAI' ? 'Retirer le chai' : 'Nommer maître de chai'}
                    </Button>
                  ) : null}
                  <Button
                    variant="ghost"
                    onClick={() =>
                      void act(() =>
                        member.status === 'ACTIVE'
                          ? api.admin.disableMember(member.id)
                          : api.admin.enableMember(member.id),
                      )
                    }
                  >
                    {member.status === 'ACTIVE' ? 'Désactiver' : 'Réactiver'}
                  </Button>
                  <Button variant="ghost" onClick={() => void act(() => api.admin.deleteMember(member.id))}>
                    Supprimer
                  </Button>
                </>
              ) : null}
            </li>
          ))}
        </ul>
        <Muted>
          Les notes d’un membre désactivé restent visibles. À la suppression, elles sont
          anonymisées.
        </Muted>
      </section>
    </div>
  );
}

/**
 * Le lien d'une invitation qu'on vient d'émettre.
 *
 * Le cercle n'a pas d'inscription publique : ce lien est l'unique porte d'entrée. Quand le
 * courrier n'arrive pas — certains fournisseurs refusent l'IP du relais sans que rien ne
 * soit fautif dans le message —, c'est par ici qu'on fait entrer quelqu'un.
 *
 * Il ne s'affiche qu'une fois. Le jeton est haché en base : rechargez la page et il est
 * perdu, il faudra renvoyer l'invitation pour en obtenir un neuf. D'où la fermeture
 * explicite plutôt qu'une disparition automatique — c'est au porteur du secret de décider
 * qu'il n'en a plus besoin.
 */
function IssuedInvitation({
  invitation,
  onDismiss,
}: {
  invitation: IssuedInvitationView;
  onDismiss: () => void;
}): ReactNode {
  return (
    <Card className={styles.issued}>
      <div className={styles.issuedHead}>
        <Kicker>Invitation émise</Kicker>
        <strong className={styles.rowEmail}>{invitation.email}</strong>
      </div>

      <CopyField value={invitation.link} label={`Lien d’invitation pour ${invitation.email}`} />

      <Muted>
        {invitation.mailed
          ? 'Le mail est parti. Si la personne ne le reçoit pas, envoyez-lui ce lien directement.'
          : 'Le mail n’a pas pu partir : transmettez ce lien vous-même, c’est la seule voie.'}{' '}
        Valable {invitation.daysLeft} jour{invitation.daysLeft > 1 ? 's' : ''}, à usage unique —
        il ouvre le cercle, ne le transmettez qu’à la personne concernée.
      </Muted>

      <Button variant="ghost" onClick={onDismiss}>
        J’ai copié le lien
      </Button>
    </Card>
  );
}
